import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lightbulb, Check, X, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuggestionRow = ({ s, label, onAccept, onDismiss, busy }) => {
  const key = `${s.vendor_lower}-${s.type}`.replace(/[^a-z0-9-]/gi, "_");
  return (
    <div className="flex items-center gap-3 py-2.5 text-sm" data-testid={`suggestion-${key}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-stone-800">{s.vendor}</span>
          <span className="text-stone-400">→</span>
          <span className="text-xs px-2 py-0.5 rounded bg-stone-100 text-stone-700">{label(s.category)}</span>
          <span className="text-[10px] uppercase tracking-wide text-stone-400">{s.type}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${s.source === "ai" ? "text-violet-700 border-violet-200 bg-violet-50" : "text-sky-700 border-sky-200 bg-sky-50"}`}>
            {s.source === "ai" ? "AI" : "Pattern"} · {Math.round(s.confidence * 100)}%
          </span>
        </div>
        <p className="text-xs text-stone-500 mt-0.5">{s.reason} {s.to_change > 0 && <span className="text-amber-700">Refiles {s.to_change} of {s.txn_count} transaction{s.txn_count !== 1 ? "s" : ""}.</span>}</p>
      </div>
      <Button size="sm" className="h-7 px-2.5 text-xs bg-stone-900 text-white hover:bg-stone-800" disabled={busy} onClick={() => onAccept(s)} data-testid={`suggestion-accept-${key}`}>
        <Check className="w-3.5 h-3.5 mr-1" /> Accept
      </Button>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-stone-500" disabled={busy} onClick={() => onDismiss(s)} data-testid={`suggestion-dismiss-${key}`}>
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
};

export const SuggestedRulesCard = ({ categories, onChange }) => {
  const [data, setData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const label = (v) => categories.find(c => c.value === v)?.label || v;

  const load = async () => {
    const res = await axios.get(`${API}/category-rules/suggestions`);
    setData(res.data);
    return res.data;
  };

  const runAi = async () => {
    setAiLoading(true);
    try { await axios.post(`${API}/category-rules/suggestions/ai`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "AI suggestions failed"); }
    finally { setAiLoading(false); }
  };

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    load().then(d => { if (!d.ai_generated_at) runAi(); }).catch(() => { setData({ suggestions: [] }); toast.error("Could not load rule suggestions"); });
  }, []);

  const accept = async (s) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/category-rules`, { vendor: s.vendor, category: s.category, type: s.type, apply_existing: true });
      toast.success(`Rule saved: ${s.vendor} → ${label(s.category)} (${res.data.updated_count} refiled)`);
      onChange(); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save rule"); }
    finally { setBusy(false); }
  };

  const dismiss = async (s) => {
    setBusy(true);
    try { await axios.post(`${API}/category-rules/suggestions/dismiss`, { vendor: s.vendor, type: s.type, category: s.category }); await load(); }
    catch (e) { toast.error("Could not dismiss"); }
    finally { setBusy(false); }
  };

  const list = data?.suggestions || [];
  return (
    <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] mt-6" data-testid="suggested-rules-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            <Lightbulb className="w-4 h-4 text-amber-500" /> Suggested Rules
            {list.length > 0 && <span className="text-xs font-sans font-medium text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">{list.length}</span>}
          </h2>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={runAi} disabled={aiLoading} data-testid="refresh-ai-suggestions-btn">
            {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : data?.ai_generated_at ? <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            {aiLoading ? "Asking AI..." : "Refresh AI suggestions"}
          </Button>
        </div>
        <p className="text-sm text-stone-500 mb-4">Based on how you already file each provider. Accepting saves the rule and refiles that provider's past transactions.</p>
        {data === null ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" /></div>
          : list.length === 0 ? <p className="text-sm text-stone-400 py-4 text-center" data-testid="suggested-rules-empty">{aiLoading ? "Looking for patterns..." : "No suggestions right now"}</p>
          : <div className="divide-y divide-stone-100">{list.map(s => <SuggestionRow key={`${s.vendor_lower}-${s.type}-${s.category}`} s={s} label={label} onAccept={accept} onDismiss={dismiss} busy={busy} />)}</div>}
        {data?.ai_generated_at && <p className="text-[11px] text-stone-400 mt-3">AI suggestions from {new Date(data.ai_generated_at).toLocaleString()}</p>}
      </CardContent>
    </Card>
  );
};
